import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import isHotKey from 'is-hotkey';
import { signOut } from 'next-auth/client';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API, useSocketComment, useSocketNotification, useSocketSyncUser } from 'src/api';
import { useSettings } from 'src/api/hooks/fetch-settings';
import Analytics from 'src/api/services/analytics/analytics';
import { useLoaderStore } from 'src/components/loaders';
import { useAuth } from 'src/shared/hooks/use-auth';
import { RootState } from 'src/store/reducers';
import { TOGGLE_SIDEBAR } from 'src/store/types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const { useEffect } = React;

interface LayoutState {
  isNotificationsOpened: boolean;
  setIsNotificationsOpened: (value: boolean) => void;

  workspaceModal: { create: boolean; settings: boolean; showSettings: boolean };
  setWorkspaceModal: (value: boolean, mode: string, showSettings?: boolean) => void;

  liveActionType: string | null;
  setLiveActionType: (value: string | null) => void;

  isSettingsOpened: boolean;
  setIsSettingsOpened: (value: boolean) => void;

  isHelpOpened: boolean;
  setIsHelpOpened: (value: boolean) => void;

  currentSettingsMenu: string;
  setCurrentSettingsMenu: (value: string) => void;

  notificationCount: number;
  setNotificationCount: (value: number) => void;
  incrementNotificationCount: () => void;
  decrementNotificationCount: () => void;

  isSearchOpen: boolean;
  setIsSearchOpen: (value: boolean) => void;

  isNewFileOpen: boolean;
  setIsNewFileOpen: (value: boolean) => void;

  // isStopTimerModalOpen: boolean;
  // setIsStopTimerModalOpen: (value: boolean) => void;

  tooltipNavigation: string;
  setTooltipNavigation: (value: string) => void;

  destinationWorkspace: { label: string; value: string };
  setDestinationWorkspace: (value: { label: string; value: string }) => void;

  splitView: boolean;
  setSplitView: (value: boolean) => void;
  splitViewFirstFile: { view: string; id: string; name: string };
  splitViewSecondFile: { view: string; id: string; name: string };
  setSplitViewFirstFile: (value: { view: string; id: string; name: string }) => void;
  setSplitViewSecondFile: (value: { view: string; id: string; name: string }) => void;
}

export const useLayoutState = create<
  LayoutState,
  [['zustand/devtools', LayoutState], ['zustand/immer', LayoutState]]
>(
  devtools(
    zustandImmerMiddleware((set) => ({
      isNotificationsOpened: false,
      setIsNotificationsOpened: (d) =>
        set((s) => {
          s.isNotificationsOpened = d;
        }),

      liveActionType: null,
      setLiveActionType: (value) =>
        set((s) => {
          s.liveActionType = value;
        }),
      isSettingsOpened: false,
      setIsSettingsOpened: (d) =>
        set((s) => {
          s.isSettingsOpened = d;
        }),
      isHelpOpened: false,
      setIsHelpOpened: (d) =>
        set((s) => {
          s.isHelpOpened = d;
        }),
      currentSettingsMenu: '',
      setCurrentSettingsMenu: (d) =>
        set((s) => {
          s.currentSettingsMenu = d;
        }),
      notificationCount: 0,
      setNotificationCount: (d) =>
        set((s) => {
          s.notificationCount = d;
        }),

      incrementNotificationCount: () =>
        set((state) => ({ notificationCount: state.notificationCount + 1 })),
      decrementNotificationCount: () =>
        set((state) => ({ notificationCount: state.notificationCount - 1 })),
      isSearchOpen: false,
      setIsSearchOpen: (d) =>
        set((s) => {
          s.isSearchOpen = d;
        }),

      isNewFileOpen: false,
      setIsNewFileOpen: (d) =>
        set((s) => {
          s.isNewFileOpen = d;
        }),

      // isStopTimerModalOpen: false,
      // setIsStopTimerModalOpen: (d) =>
      //   set((s) => {
      //     s.isStopTimerModalOpen = d;
      //   }),

      tooltipNavigation: 'home',
      setTooltipNavigation: (d) =>
        set((s) => {
          s.tooltipNavigation = d;
        }),

      workspaceModal: { create: false, settings: false, showSettings: false },
      setWorkspaceModal: (value, mode, showSettings) =>
        set((s) => {
          if (showSettings) {
            s.workspaceModal[mode] = 'general';
            s.workspaceModal['showSettings'] = showSettings;
          } else {
            s.workspaceModal[mode] = value;
            s.workspaceModal['showSettings'] = false;
          }
        }),

      destinationWorkspace: { label: '', value: '' },
      setDestinationWorkspace: (value) =>
        set((s) => {
          s.destinationWorkspace = value;
        }),

      splitView: false,
      setSplitView: (value) =>
        set((s) => {
          s.splitView = value;
        }),

      splitViewFirstFile: { view: '', id: '', name: '' },
      setSplitViewFirstFile: (value) =>
        set((s) => {
          s.splitViewFirstFile = value;
        }),

      splitViewSecondFile: { view: '', id: '', name: '' },
      setSplitViewSecondFile: (value) =>
        set((s) => {
          s.splitViewSecondFile = value;
        }),
    }))
  )
);

export const useLayout = () => {
  const {
    isNotificationsOpened,
    setIsNotificationsOpened,
    isSettingsOpened,
    setIsSearchOpen,
    isSearchOpen,
    setIsSettingsOpened,
    isHelpOpened,
    setIsHelpOpened,
    notificationCount,
    setNotificationCount,
    incrementNotificationCount,
    decrementNotificationCount,
    currentSettingsMenu,
    setCurrentSettingsMenu,
    tooltipNavigation,
    setTooltipNavigation,
    isNewFileOpen,
    setIsNewFileOpen,
    // isStopTimerModalOpen,
    // setIsStopTimerModalOpen,
    destinationWorkspace,
    setDestinationWorkspace,
    splitView,
    setSplitView,
    splitViewFirstFile,
    splitViewSecondFile,
    setSplitViewFirstFile,
    setSplitViewSecondFile,
    liveActionType,
    setLiveActionType,
  } = useLayoutState();
  const authData = useAuth((s) => s.userDetails);

  const { setIsLoading } = useLoaderStore();
  const fetchUserDetails = useAuth((s) => s.fetchUserDetails);
  const dispatch = useDispatch();
  const { isSidebarOpened } = useSelector((state: RootState) => state.layout);
  const socketNotification = useSocketNotification();
  const socketComment = useSocketComment();
  const socketSyncUser = useSocketSyncUser({ user: authData?.userId });
  const { revalidate: settingsRevalidate } = useSettings();

  useEffect(() => {
    const handleShortcuts = (e) => {
      if (isHotKey('mod+shift+H', e)) {
        e.preventDefault();
        onOpenHelp();
      } else if (isHotKey('mod+shift+S', e)) onOpenSettings();
      else if (isHotKey('mod+P', e)) {
        e.preventDefault();
        onSearchOpen();
      }
    };

    document.addEventListener('keydown', handleShortcuts);
    return () => document.removeEventListener('keydown', handleShortcuts);
  }, []);

  function onToggleSideBar() {
    dispatch({ type: TOGGLE_SIDEBAR });
  }
  function onOpenNotifications() {
    setIsNotificationsOpened(true);
    Analytics.track('Notification View');
  }
  function onCloseNotifications() {
    setIsNotificationsOpened(false);
  }
  function onOpenNotificationSettings() {
    setCurrentSettingsMenu('preferences');
    setIsSettingsOpened(true);
    setIsHelpOpened(false);
    setIsNotificationsOpened(false);
  }
  function onOpenSettings() {
    setIsSettingsOpened(true);
    setIsHelpOpened(false);
    setIsNotificationsOpened(false);
    Analytics.track('Settings Click');
  }
  function onCloseSettings() {
    setIsSettingsOpened(false);
    settingsRevalidate();
  }
  function onCloseHelp() {
    setIsHelpOpened(false);
  }
  function onOpenHelp() {
    setIsSettingsOpened(false);
    setIsHelpOpened(true);
    Analytics.track('Help Center Click');
  }
  //Fetch notification count to update
  async function onFetchNotificationCount() {
    const notificationCount = await API.get(`user/notifications/count`);
    if (notificationCount) {
      setNotificationCount(notificationCount.data);
    }
  }

  async function onChangeOrganisation(value) {
    setIsLoading(true);

    const response = await API.put('/settings/organisation/select', {
      organisation: value.organisationId,
    });
    if (response?.data?.success) {
      setIsLoading(false);
    }
    fetchUserDetails();

    location.href = `/${'w'}/${value.organisationId}/dashboard`;
  }

  const onLogout = async () => {
    Analytics.track('Logs Out');
    Analytics.reset();
    await signOut();
    window.localStorage.removeItem('url');
  };

  function onProfileMenu(e) {
    if (e.value === 'settings-member') {
      setCurrentSettingsMenu('profile');
      onOpenSettings();
    }
    if (e.value === 'preferences') {
      setCurrentSettingsMenu('preferences');
      onOpenSettings();
    }
    if (e.value === 'log-out') {
      onLogout();
    }
  }
  function onSearchOpen() {
    setIsSearchOpen(true);
  }
  function onSearchClose() {
    setIsSearchOpen(false);
  }

  function onNewFileOpen() {
    setIsNewFileOpen(true);
  }
  function onNewFileClose() {
    setIsNewFileOpen(false);
  }

  function onToolTipNavigation(e) {
    switch (e) {
      case 0:
        setTooltipNavigation('home');
        break;
      case 1:
        setTooltipNavigation('my-lessons');
        break;
      case 2:
        setTooltipNavigation('creations');
        break;
      case 3:
        setTooltipNavigation('docs');
        break;
      case 4:
        setTooltipNavigation('boards');
        break;
      default:
        setTooltipNavigation('tooltipNavigation');
    }
  }
  return {
    onProfileMenu,
    onLogout,
    onChangeOrganisation,
    onOpenHelp,
    onCloseHelp,
    onCloseSettings,
    onFetchNotificationCount,
    onOpenSettings,
    onOpenNotificationSettings,
    onCloseNotifications,
    onOpenNotifications,
    onToggleSideBar,
    onSearchOpen,
    onSearchClose,
    isNotificationsOpened,
    isSettingsOpened,
    isHelpOpened,
    currentSettingsMenu,
    notificationCount,
    isSidebarOpened,
    isSearchOpen,
    tooltipNavigation,
    onToolTipNavigation,
    onNewFileOpen,
    onNewFileClose,
    isNewFileOpen,
    setNotificationCount,
    incrementNotificationCount,
    decrementNotificationCount,
    // isStopTimerModalOpen,
    // setIsStopTimerModalOpen,
    setDestinationWorkspace,
    destinationWorkspace,
    splitView,
    setSplitView,
    splitViewFirstFile,
    splitViewSecondFile,
    setSplitViewFirstFile,
    setSplitViewSecondFile,
    liveActionType,
    setLiveActionType,
  };
};
